/**
 * PanelFunctions.js
 * 
 * This module provides utility functions for managing solar panels in the Eagle Eye Designer application.
 * It contains functions for selecting, deselecting, deleting, and validating panels, as well as
 * changing panel orientation and cleaning up visual elements.
 * 
 * These functions work in conjunction with the PanelManager class to provide a complete
 * panel management system for the application.
 */

import { COLORS } from "../Global/materials";
import { POLYGONS } from "../Global/variables";
import { sceneManager } from "../SetUps/SceneManager";
import PanelManager from "./PanelManager";
import { panelInSegment } from "./Placement";

/**
 * Selects all panels in the currently selected segment
 * 
 * This function highlights all panels in the current segment with a yellow outline
 * and updates the selection state to include all panel indices.
 * 
 * @param {Object} selectedIndices - Object containing current selection indices
 * @param {Function} setSelectedIndices - State setter function for selection indices
 * @param {Function} setShowPanelSettings - State setter function to control panel settings visibility
 */
export function selectAllPanels(selectedIndices, setSelectedIndices, setShowPanelSettings) {
  // Get the currently selected segment index
  const { segmentIndex } = selectedIndices;
  
  // Get all panels in the segment
  const panels = POLYGONS[segmentIndex].panels;
  
  // Array to store indices of all panels
  let indices = [];
  
  // Loop through all panels and highlight them
  for (let i = 0; i < panels.length; i++) {
    const panel = panels[i].panel;
    
    // Set yellow outline with full opacity for selected panels
    panel.children[0].material.setValues({ opacity: 1, color: COLORS.yellow });
    
    // Show the panel settings UI
    setShowPanelSettings(true);
    
    // Add the panel index to the selected indices
    indices.push(i);
  }
  
  // Update the selection state with all panel indices
  setSelectedIndices({ segmentIndex: segmentIndex, obstacleIndex: null, panelIndex: indices });
}

/**
 * Selects a single panel in the currently selected segment
 * 
 * This function highlights the specified panel and updates the selection state.
 * It can either replace the current selection or add to it.
 * 
 * @param {number} panelIndexToAdd - Index of the panel to select
 * @param {Function} setSelectedIndices - State setter function for selection indices
 * @param {Object} selectedIndices - Object containing current selection indices
 * @param {Function} setShowPanelSettings - State setter function to control panel settings visibility
 */
export function selectSinglePanel(panelIndexToAdd, setSelectedIndices, selectedIndices, setShowPanelSettings) {
  // Get current selection information
  const { segmentIndex, panelIndex } = selectedIndices;
  // Get the segment and panel to select
  const segment = POLYGONS[segmentIndex] ?? null;
  const panel = segment.panels[panelIndexToAdd].panel ?? null;

  // Only proceed if panel exists
  if (panel) {
    // Set yellow outline with full opacity for selected panel
    panel.children[0].material.setValues({ opacity: 1, color: COLORS.yellow });
    
    // Show the panel settings UI
    setShowPanelSettings(true);
    
    // If no panel is currently selected or multi-select is not active,
    // create a new selection with just this panel
    if (!panelIndex) {
      setSelectedIndices({ segmentIndex: segmentIndex, obstacleIndex: null, panelIndex: [panelIndexToAdd] });
    } else {
      // Otherwise, add this panel to the existing selection
      setSelectedIndices({ segmentIndex: segmentIndex, obstacleIndex: null, panelIndex: [...panelIndex, panelIndexToAdd] });
    }
  }
}

/**
 * Deselects specified panels in the currently selected segment
 * 
 * This function removes the highlight from the specified panels and updates the selection state.
 * 
 * @param {Array|null} indices - Array of panel indices to deselect, or null to deselect all
 * @param {Function} setSelectedIndices - State setter function for selection indices
 * @param {Object} selectedIndices - Object containing current selection indices
 */
export function deselectPanels(indices, setSelectedIndices, selectedIndices) {  
  // Only proceed if indices is provided
  if (indices) {
    // Get current selection information
    const { segmentIndex, obstacleIndex, panelIndex } = selectedIndices;
    
    // Get panels in the segment
    const panels = POLYGONS[segmentIndex].panels ?? null;
    
    // Only proceed if panels exist
    if (panels) {
      // Loop through each index to deselect
      for (let index of indices) {
        // Hide outline for deselected panels
        panels[index].panel.children[0].material.setValues({ opacity: 0, color: COLORS.red });
      }
      
      // Filter out deselected indices from the current selection
      const updated = panelIndex.filter(val => !indices.includes(val));
      
      // If no panels remain selected, set panelIndex to null
      if (updated.length === 0) {
        setSelectedIndices({ segmentIndex: segmentIndex, obstacleIndex: obstacleIndex, panelIndex: null });
      } else {
        // Otherwise, update with the remaining selected panels
        setSelectedIndices({ segmentIndex: segmentIndex, obstacleIndex: null, panelIndex: updated });
      }
    }
  }
}

/**
 * Deletes specified panels from a segment
 * 
 * This function removes the panel meshes from the scene and updates the POLYGONS data structure.
 * 
 * @param {number} segmentIndex - Index of the segment containing the panels
 * @param {Array} panelIndices - Array of panel indices to delete
 */
export function deletePanels(segmentIndex, panelIndices) {
  // Get the segment containing the panels
  let selectedSegment = POLYGONS[segmentIndex];
  
  // Get the scene to remove objects from
  let scene = sceneManager.scene;
  
  // Loop through each panel index to delete
  for (let index of panelIndices) {
    // Get the panel to delete
    const selectedPanel = selectedSegment.panels[index].panel;
    
    // Remove the panel from the scene
    scene.remove(selectedPanel);
    
    // Dispose of geometry and material to free memory
    selectedPanel.geometry.dispose();
    selectedPanel.material.dispose();
  }
  
  // Update the panels array to exclude deleted panels
  selectedSegment.panels = selectedSegment.panels.filter((_, index) => !panelIndices.includes(index));
}

/**
 * Removes panels from the scene that are not referenced in the POLYGONS data structure
 * 
 * This cleanup function ensures no orphaned panel meshes remain in the scene.
 * 
 * @param {Array} polygons - The POLYGONS array to check against
 */
export function removeExtraPanels(polygons) {
  // Array to store all panel meshes found in the scene
  const allPanels = [];
  
  // Flag to track if a panel was found in the POLYGONS data
  let found = false;
  
  // Array to store panels that need to be removed
  let selected = [];
  
  // Get all objects named "panel" from the scene
  sceneManager.scene.getObjectsByProperty("name", "panel", allPanels);
  
  // Check each panel to see if it's referenced in the POLYGONS data
  for (let panel of allPanels) {
    found = false;
    const idToMatch = panel.id;
    selected.push(panel);
    
    // Check each polygon for the panel
    for (let polygon of polygons) {
      const panelList = polygon.panels;
      for (let index of panelList) {
        // If panel is found in the data, don't remove it
        if (index.panel_id === idToMatch) {
          found = true;
          selected.pop();
          break;
        }
      }
      if (found === true) break;
    }
  }

  // Remove all panels that weren't found in the POLYGONS data
  for (let i = 0; i < selected.length; i++) {
    sceneManager.scene.remove(selected[i]);
    selected[i].geometry.dispose();
    selected[i].material.dispose();
  }
}

/**
 * Removes all grid helper meshes from the scene
 * 
 * This function cleans up grid visuals that are used for panel alignment.
 */
export function removeGrid() {
  // Get all objects named "grid" from the scene
  const grids = sceneManager.scene.getObjectsByProperty("name", "grid");
  
  // Remove each grid if found
  if (grids) {
    for (let grid of grids) {
      grid.removeFromParent();
      grid.geometry.dispose();
      grid.material.dispose();
    }
  }
}

/**
 * Changes the orientation of a panel (portrait to landscape or vice versa)
 * 
 * This function creates a new panel with the desired orientation at the same location,
 * then deletes the original panel.
 * 
 * @param {THREE.Scene} scene - The scene where the panel is rendered
 * @param {THREE.Camera} camera - The camera for raycasting and positioning
 * @param {number} segmentIndex - Index of the segment containing the panel
 * @param {Object} solarPanel - Object containing panel specifications with updated orientation
 * @param {Function} setPanels - Function to update panel list in state
 * @param {Array} panelList - Array of available panel types/models
 * @param {THREE.Mesh} panel - The current panel mesh to replace
 * @param {number} panelIndex - Index of the panel to replace
 * @param {Function} deletePanel - Function to update panel deletion state
 * @param {Function} setPanelsOK - Function to update panel validity state
 * @param {boolean} arePanelsOK - Current panel validity state
 * @returns {Promise} A promise that resolves when the operation is complete
 */
export async function changeOrientation(scene, camera, segmentIndex, solarPanel, setPanels, panelList, panel, panelIndex, deletePanel, setPanelsOK, arePanelsOK) {
  // Create a new panel manager with the updated orientation
  const newPanel = new PanelManager(
    scene,
    camera,
    segmentIndex,
    solarPanel,
    setPanels,
    panelList,
    null,
    null, 
    true
  );
  
  // Position the new panel at the same location as the old one
  await newPanel.handleMouseMove(panel.position.project(camera));
  await newPanel.handleMouseMove(newPanel.panel.position.project(camera));
  
  // Add the new panel to the segment
  newPanel.addPanel();

  // Remove the old panel
  deletePanels(segmentIndex, [panelIndex]);
  deletePanel([panelIndex]);

  // Get the newly created panel for validation
  let totalPanels = POLYGONS[segmentIndex].panels.length;
  let current = POLYGONS[segmentIndex].panels[totalPanels - 1].panel;
  
  // Update visual feedback based on placement validity
  if (!panelInSegment(current, segmentIndex)) {
    current.children[0].material.setValues({ opacity: 1, color: COLORS.red });
  } else {
    current.children[0].material.setValues({ opacity: 1, color: COLORS.yellow });
  }
  
  // Check if all panels in the segment are valid
  checkPanelsOK(segmentIndex, setPanelsOK, arePanelsOK);
}

/**
 * Checks if all panels in a segment are validly placed
 * 
 * This function validates each panel's placement and updates the panelsOK state.
 * 
 * @param {number} segmentIndex - Index of the segment to check
 * @param {Function} setPanelsOK - Function to update panel validity state
 * @param {boolean} arePanelsOK - Current panel validity state
 */
export function checkPanelsOK(segmentIndex, setPanelsOK, arePanelsOK) {
  // Check each panel in the segment
  for(let panel of POLYGONS[segmentIndex].panels) {
    // If panel is valid, set panelsOK to true
    if (panelInSegment(panel.panel, segmentIndex)) {
      setPanelsOK(true);      
    } else {
      // If any panel is invalid, set panelsOK to false and stop checking
      setPanelsOK(false);      
      break;
    }
    
    // Stop checking if panelsOK is already false
    if(!arePanelsOK) break;
  }
}